import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { checkUserSubscription } from '../services/stripe';

interface SubscriptionContextType {
  isSubscribed: boolean;
  isTrialing: boolean;
  status: string;
  plan: string | null;
  trialEnd: Date | null;
  currentPeriodEnd: Date | null;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription 必须在 SubscriptionProvider 内部使用');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState({
    isSubscribed: false,
    isTrialing: false,
    status: 'inactive',
    plan: null as string | null,
    trialEnd: null as Date | null,
    currentPeriodEnd: null as Date | null,
  });

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription({
        isSubscribed: false,
        isTrialing: false,
        status: 'inactive',
        plan: null,
        trialEnd: null,
        currentPeriodEnd: null,
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const subscriptionData = await checkUserSubscription(user.uid);
      
      setSubscription({
        isSubscribed: subscriptionData.isSubscribed,
        isTrialing: subscriptionData.isTrialing,
        status: subscriptionData.status,
        plan: subscriptionData.plan || null,
        trialEnd: subscriptionData.trialEnd || null,
        currentPeriodEnd: subscriptionData.currentPeriodEnd || null,
      });
    } catch (error) {
      console.error('获取订阅信息时出错:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // 当用户状态变更时获取订阅信息
  useEffect(() => {
    if (!authLoading) {
      void fetchSubscription();
    }
  }, [authLoading, fetchSubscription]);

  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  const value = {
    ...subscription,
    loading,
    refreshSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 
